import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { OtpLogin } from "@/components/OtpLogin";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("password");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="login-page">
      <div className="grain-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" data-testid="login-logo-link" className="flex flex-col items-center gap-4 mb-12">
          <img src="/logo-mark.png" alt="MetrixWorks logo" className="h-16 w-16 bg-white p-1" data-testid="login-logo-image" />
          <span className="flex items-baseline gap-2">
            <span className="font-display text-2xl">MetrixWorks</span>
            <span className="font-accent text-[10px] tracking-[0.25em] text-[#D4AF37] uppercase">Service Pvt Ltd</span>
          </span>
        </Link>
        <div className="bg-[#0A0A0A] border border-white/10 p-10">
          <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/35 mb-2">Investor Access</p>
          <h1 className="font-display text-3xl tracking-tight mb-8">Welcome back.</h1>
          <div className="flex border border-white/10 mb-8" data-testid="login-mode-tabs">
            {[["password", "Email & Password"], ["otp", "Mobile + Code"]].map(([k, label]) => (
              <button key={k} type="button" data-testid={`login-mode-${k}`} onClick={() => { setMode(k); setError(""); }} className={`flex-1 py-3 font-accent text-[10px] tracking-[0.2em] uppercase transition-colors ${mode === k ? "bg-[#D4AF37] text-black" : "text-white/50 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
          {mode === "otp" && <OtpLogin />}
          {mode === "password" && error && (
            <p data-testid="login-error" className="mb-6 text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>
          )}
          {mode === "password" && <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <input
              data-testid="login-email-input"
              type="email"
              required
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
            <input
              data-testid="login-password-input"
              type="password"
              required
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls}
            />
            <button
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>}
          {mode === "password" && <p className="mt-4 text-right">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-white/40 hover:text-[#D4AF37] transition-colors">
              Forgot password?
            </Link>
          </p>}
          <p className="mt-6 text-sm text-white/40">
            New to MetrixWorks?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-[#D4AF37] hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
